import { Box, Chip, Divider, Grid, Link, Paper, Theme, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { Helmet } from 'react-helmet';
import { FileFormat, TransactionStatement } from '../../gql-types.generated';
import { allMonths } from '../../util/Months';
import { selectTenantAccount } from '../app/AppSlice';
import {
  clearGenerateStatementAccess,
  selectGenerateStatementAccessUrl,
  selectGenerateStatementAccessUrlError,
  selectGenerateStatementAccessUrlInFlight,
} from '../home/HomeSlice';
import { fetchGenerateStatementAccessUrl } from '../home/HomeActions';
import LoadingMask from '../../components/LoadingMask';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexFlow: 'column nowrap',
      flex: 1,
      height: '100%',
      minHeight: 350,
    },
    header: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'start',
      padding: theme.spacing(2.5),
    },
    grid: {
      padding: theme.spacing(3),
    },
    yearFilters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(3),
    },
    yearChipSelected: {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.uxBlue.activated,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: theme.palette.uxBlue.activated,
      },
    },
    statementList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
    statementItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: `1px solid ${theme.palette.uxGrey.border}`,
      borderRadius: 10,
      padding: theme.spacing(2, 2.5),
      backgroundColor: theme.palette.background.paper,
    },
    statementInfo: {
      display: 'flex',
      flexDirection: 'column',
    },
    statementRange: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.25),
    },
    downloadLinks: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
    downloadLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      cursor: 'pointer',
      fontWeight: 500,
    },
    downloadIcon: {
      fontSize: '1.1rem',
    },
    emptyState: {
      padding: theme.spacing(4),
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    errorBanner: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.main,
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 3),
    },
    errorIcon: {
      marginRight: 4,
    },
  }),
);

const Statements: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const tenantAccount = useSelector(selectTenantAccount);
  const statementUrl = useSelector(selectGenerateStatementAccessUrl);
  const statementUrlError = useSelector(selectGenerateStatementAccessUrlError);
  const isFetchingStatementUrl = useSelector(selectGenerateStatementAccessUrlInFlight);

  const statements = tenantAccount?.statements;
  const downloadRef = useRef<HTMLAnchorElement>(null);
  const [activeDownload, setActiveDownload] = useState<string | null>(null);

  // Distinct years present in the statements, most recent first.
  const years = useMemo(() => {
    const yearSet = new Set<number>();
    (statements || []).forEach(statement => {
      yearSet.add(DateTime.fromISO(statement.endTime).toUTC().year);
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [statements]);

  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);

  // Default the selected year to the most recent year with statements.
  useEffect(() => {
    if (years.length > 0 && (selectedYear === undefined || !years.includes(selectedYear))) {
      setSelectedYear(years[0]);
    }
  }, [years]);

  // Statements for the selected year, sorted most recent first.
  const yearStatements = useMemo(() => {
    if (selectedYear === undefined) return [];
    return (statements || [])
      .filter(statement => DateTime.fromISO(statement.endTime).toUTC().year === selectedYear)
      .slice()
      .sort((a, b) => (DateTime.fromISO(a.endTime) > DateTime.fromISO(b.endTime) ? -1 : 1));
  }, [statements, selectedYear]);

  const handleDownload = (fileName?: string) => {
    if (fileName) {
      setActiveDownload(fileName);
      dispatch(fetchGenerateStatementAccessUrl(fileName));
    }
  };

  // Once the access URL for the requested statement resolves, trigger the browser download.
  useEffect(() => {
    if (statementUrl && activeDownload && downloadRef.current) {
      downloadRef.current.href = statementUrl;
      downloadRef.current.click();
      setActiveDownload(null);
      dispatch(clearGenerateStatementAccess());
    }
  }, [statementUrl, activeDownload, dispatch]);

  useEffect(
    () => () => {
      dispatch(clearGenerateStatementAccess());
    },
    [dispatch],
  );

  const getStatementLabel = (statement: TransactionStatement) => {
    const end = DateTime.fromISO(statement.endTime).toUTC();
    return `${allMonths[end.month - 1].label} ${end.year}`;
  };

  const getStatementRange = (statement: TransactionStatement) => {
    const start = DateTime.fromISO(statement.startTime).toUTC();
    const end = DateTime.fromISO(statement.endTime).toUTC();
    // e.g. "Apr 1–30" when within the same month, otherwise "Apr 1 – May 2".
    if (start.month === end.month && start.year === end.year) {
      return `${start.toFormat('MMM d')}–${end.toFormat('d')}`;
    }
    return `${start.toFormat('MMM d')} – ${end.toFormat('MMM d')}`;
  };

  return (
    <Paper className={classes.root} role="region" aria-label="Statements">
      <Helmet>
        <meta name="ai:viewId" content="statements"></meta>
        <meta name="ai:viewDescription" content="Aptean Pay Merchant Portal - Statements"></meta>
        <title>Aptean Pay Merchant Portal - Statements</title>
      </Helmet>
      <LoadingMask loading={isFetchingStatementUrl} />
      <a ref={downloadRef} style={{ display: 'none' }} />
      <Box className={classes.header}>
        <Typography variant="title">Statements</Typography>
      </Box>
      <Divider />
      {statementUrlError && (
        <Box className={classes.errorBanner}>
          <ErrorIcon className={classes.errorIcon} />
          {`${statementUrlError?.message || 'Unable to generate statement'}. Please try again later!`}
        </Box>
      )}
      <Grid className={classes.grid} container direction="column">
        {years.length > 0 && (
          <Box className={classes.yearFilters} data-cy="statement-year-filters">
            {years.map(year => (
              <Chip
                key={year}
                label={year}
                variant="outlined"
                clickable
                onClick={() => setSelectedYear(year)}
                className={selectedYear === year ? classes.yearChipSelected : undefined}
                data-cy={`statement-year-${year}`}
              />
            ))}
          </Box>
        )}
        {yearStatements.length > 0 ? (
          <Box className={classes.statementList} data-cy="statement-list">
            {yearStatements.map(statement => {
              // Backend returns CSV, HTML and PDF; only CSV and PDF are offered for download here.
              const formats = (statement.availableFormats || []).filter(format => format.format !== FileFormat.Html);
              return (
                <Box className={classes.statementItem} key={statement.endTime} data-cy="statement-item">
                  <Box className={classes.statementInfo}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {getStatementLabel(statement)}
                    </Typography>
                    <Typography variant="body2" className={classes.statementRange}>
                      {getStatementRange(statement)}
                    </Typography>
                  </Box>
                  <Box className={classes.downloadLinks}>
                    {formats.length > 0 ? (
                      formats.map(format => (
                        <Link
                          key={format.format}
                          component="button"
                          type="button"
                          underline="hover"
                          className={classes.downloadLink}
                          onClick={() => handleDownload(format.fileName)}
                          data-cy={`statement-download-${format.format}`}
                        >
                          <FileDownloadOutlinedIcon className={classes.downloadIcon} />
                          {format.format === FileFormat.Pdf ? 'PDF' : format.format}
                        </Link>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Unavailable
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box className={classes.emptyState} data-cy="statement-empty">
            <Typography variant="body1">No statements available.</Typography>
          </Box>
        )}
      </Grid>
    </Paper>
  );
};

export default Statements;
